import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Spinner, Empty } from "../ui";
import { Megaphone, X, CheckCircle2, XCircle, AlertTriangle, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";

function YesNo({ v, testid }) {
  return v ? (
    <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" data-testid={testid} />
  ) : (
    <XCircle className="w-4 h-4 text-gray-300 inline" data-testid={testid} />
  );
}

function MessageModal({ row, onClose }) {
  const body = row.message || null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-auto bg-white border border-gray-300 rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()} data-testid="campanii-tenanti-message-modal" style={{ fontFamily: "Arial" }}>
        <div className="sticky top-0 bg-white border-b border-gray-300 px-5 py-4 flex items-center justify-between">
          <h3 className="text-base font-bold text-black">Mesaj campanie tenant</h3>
          <button onClick={onClose} data-testid="campanii-tenanti-message-close"><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 space-y-4 text-black" style={{ fontSize: "14px" }}>
          <div className="grid grid-cols-2 gap-3">
            <div><span className="text-gray-500">Tenant</span><div className="font-bold">{row.tenant_name || "-"}</div></div>
            <div><span className="text-gray-500">Campanie</span><div className="font-bold">{row.campaign || "-"}</div></div>
            <div><span className="text-gray-500">Firma</span><div className="font-bold">{row.firma || "-"}</div></div>
            <div><span className="text-gray-500">Adresa mail</span><div className="font-bold break-all">{row.email || "-"}</div></div>
            <div className="col-span-2"><span className="text-gray-500">Subiect</span><div className="font-bold">{row.subject || "-"}</div></div>
            <div className="col-span-2">
              <span className="text-gray-500">Link activare cont</span>
              {row.activation_url ? (
                <div className="font-bold break-all">
                  <a href={row.activation_url} target="_blank" rel="noreferrer"
                    className="underline text-black hover:text-gray-600" data-testid="campanii-tenanti-activation-link">
                    {row.activation_url}
                  </a>
                </div>
              ) : (
                <div className="font-bold text-gray-500" data-testid="campanii-tenanti-activation-link">-</div>
              )}
            </div>
          </div>
          <div>
            <div className="text-gray-500 mb-1">Text mesaj trimis</div>
            {body ? (
              <pre className="whitespace-pre-wrap font-sans border border-gray-300 rounded p-3 bg-gray-50" data-testid="campanii-tenanti-message-body">{body}</pre>
            ) : (
              <div className="text-gray-500 border border-gray-300 rounded p-3" data-testid="campanii-tenanti-message-body">
                - (fara text salvat pentru aceasta campanie)
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function CampaniiTenanti() {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [online, setOnline] = useState(true);
  const [loading, setLoading] = useState(true);
  const [tenantFilter, setTenantFilter] = useState("");
  const [sel, setSel] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaign-report-tenanti");
      setItems(data.items || []);
      setTenants(data.tenants || []);
      setOnline(!!data.delivery_source_online);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = items.filter((r) => !tenantFilter || r.tenant_id === tenantFilter);

  useEffect(() => { setPage(0); }, [tenantFilter, pageSize, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sorted = React.useMemo(() => {
    if (!sortKey) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      let av = a[sortKey];
      let bv = b[sortKey];
      if (typeof av === "boolean" || typeof bv === "boolean") {
        av = av ? 1 : 0;
        bv = bv ? 1 : 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }
      av = (av ?? "").toString().toLowerCase();
      bv = (bv ?? "").toString().toLowerCase();
      const cmp = av.localeCompare(bv, "ro", { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const curPage = Math.min(page, totalPages - 1);
  const start = curPage * pageSize;
  const paged = sorted.slice(start, start + pageSize);

  const cell = "px-3 py-2 border-b border-gray-200 text-black align-top";
  const head = "px-3 py-2 border-b-2 border-gray-400 font-bold text-black text-left whitespace-nowrap";

  const SortHead = ({ label, k, center }) => {
    const active = sortKey === k;
    const Icon = !active ? ArrowUpDown : sortDir === "asc" ? ArrowUp : ArrowDown;
    return (
      <th className={head + (center ? " text-center" : "")}>
        <button type="button" onClick={() => toggleSort(k)}
          data-testid={`campanii-tenanti-sort-${k}`}
          className={"inline-flex items-center gap-1 font-bold text-black hover:text-gray-600 select-none" + (center ? " justify-center" : "")}>
          {label}
          <Icon className={"w-3 h-3 " + (active ? "text-black" : "text-gray-400")} />
        </button>
      </th>
    );
  };

  return (
    <div style={{ fontFamily: "Arial", fontSize: "14px" }} data-testid="campanii-tenanti-page">
      <PageHeader title="CAMPANII TENANTI" subtitle="Ce s-a trimis in campaniile tenantilor (distribuitor), per destinatar. Doar super admin." />

      {loading ? <Spinner /> : (
        <>
          {!online && (
            <div className="flex items-start gap-2 border border-gray-400 bg-gray-50 p-3 rounded mb-4" data-testid="campanii-tenanti-offline-note">
              <AlertTriangle className="w-4 h-4 text-gray-600 flex-shrink-0 mt-0.5" />
              <div className="text-gray-700">
                Datele de livrare (livrat, bounce, blocked, complaint, deschideri) nu sunt inca conectate.
                Se conecteaza la deploy-ul serverului de campanii. Pana atunci coloanele afiseaza "-".
              </div>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mb-4">
            <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} data-testid="campanii-tenanti-tenant-select"
              className="px-3 py-2 rounded border border-gray-300 outline-none focus:border-black text-black"
              style={{ fontFamily: "Arial", fontSize: "14px" }}>
              <option value="">Toti tenantii</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.slug})</option>)}
            </select>
          </div>

          {filtered.length === 0 ? (
            <Card><Empty icon={Megaphone} title="Nicio inregistrare" hint="Nu exista destinatari in campaniile tenantilor inca." /></Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full whitespace-nowrap" data-testid="campanii-tenanti-table" style={{ fontSize: "14px" }}>
                  <thead>
                    <tr>
                      <SortHead label="Tenant" k="tenant_name" />
                      <SortHead label="Nume campanie" k="campaign" />
                      <SortHead label="Tara" k="tara" />
                      <SortHead label="Judet" k="judet" />
                      <SortHead label="Nume firma" k="firma" />
                      <th className={head}>Mesaj</th>
                      <SortHead label="Adresa mail" k="email" />
                      <SortHead label="Livrat" k="delivered" center />
                      <SortHead label="Dezabonat" k="unsubscribed" center />
                      <SortHead label="Bounce" k="bounced" center />
                      <SortHead label="Complaint" k="complained" center />
                      <SortHead label="Blocked" k="blocked" center />
                      <SortHead label="Deschideri" k="opened" center />
                      <SortHead label="Link accesat" k="link_opened" center />
                      <SortHead label="Creat cont" k="account_created" center />
                    </tr>
                  </thead>
                  <tbody>
                    {paged.map((r, i) => (
                      <tr key={i} data-testid={`campanii-tenanti-row-${i}`} className="hover:bg-gray-50">
                        <td className={cell}>{r.tenant_name || "-"}</td>
                        <td className={cell}>{r.campaign || "-"}</td>
                        <td className={cell}>{r.tara || "-"}</td>
                        <td className={cell}>{r.judet || "-"}</td>
                        <td className={cell}>{r.firma || "-"}</td>
                        <td className={cell}>
                          <button onClick={() => setSel(r)} data-testid={`campanii-tenanti-message-btn-${i}`}
                            className="underline text-black hover:text-gray-600">Vezi mesaj</button>
                        </td>
                        <td className={cell + " break-all"} style={{ whiteSpace: "normal" }}>{r.email || "-"}</td>
                        <td className={cell + " text-center"}><YesNo v={r.delivered} testid={`campanii-tenanti-delivered-${i}`} /></td>
                        <td className={cell + " text-center"}><YesNo v={r.unsubscribed} testid={`campanii-tenanti-unsub-${i}`} /></td>
                        <td className={cell + " text-center"}><YesNo v={r.bounced} testid={`campanii-tenanti-bounce-${i}`} /></td>
                        <td className={cell + " text-center"}><YesNo v={r.complained} testid={`campanii-tenanti-complaint-${i}`} /></td>
                        <td className={cell + " text-center"}><YesNo v={r.blocked} testid={`campanii-tenanti-blocked-${i}`} /></td>
                        <td className={cell + " text-center"}><YesNo v={r.opened} testid={`campanii-tenanti-opened-${i}`} /></td>
                        <td className={cell + " text-center"}><YesNo v={r.link_opened} testid={`campanii-tenanti-linkopened-${i}`} /></td>
                        <td className={cell + " text-center"}><YesNo v={r.account_created} testid={`campanii-tenanti-account-${i}`} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-gray-500" data-testid="campanii-tenanti-count">
              Total destinatari: {filtered.length}
              {filtered.length > 0 && (
                <span> (afisez {start + 1}-{Math.min(start + pageSize, filtered.length)})</span>
              )}
            </div>
            {filtered.length > 0 && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-gray-700">
                  Pe pagina
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                    data-testid="campanii-tenanti-page-size"
                    className="px-2 py-1 rounded border border-gray-300 outline-none focus:border-black text-black"
                    style={{ fontFamily: "Arial", fontSize: "14px" }}>
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>
                <div className="flex items-center gap-2">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={curPage <= 0}
                    data-testid="campanii-tenanti-prev-page"
                    className="px-3 py-1 rounded border border-gray-300 text-black disabled:opacity-40 hover:bg-gray-100">
                    Inapoi
                  </button>
                  <span data-testid="campanii-tenanti-page-info" className="text-gray-700">
                    Pagina {curPage + 1} / {totalPages}
                  </span>
                  <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={curPage >= totalPages - 1}
                    data-testid="campanii-tenanti-next-page"
                    className="px-3 py-1 rounded border border-gray-300 text-black disabled:opacity-40 hover:bg-gray-100">
                    Inainte
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {sel && <MessageModal row={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
